import { useEffect, useState } from 'react';

import {
  EsignInvitePreviewQuery,
  useEsignInvitePreviewQuery,
} from 'core/graphql';

export type IContractInvitePreview = NonNullable<
  EsignInvitePreviewQuery['contractInvitePreview']
>;

/** ТЗ 0094: разовая правка письма — сырой текст с `{…}`. */
export interface IInviteOverride {
  subject?: string;
  body?: string;
}

/** Пауза перед пере-запросом предпросмотра, пока человек печатает. */
export const INVITE_PREVIEW_DEBOUNCE_MS = 400;

/** Через `delay` мс после последнего изменения — значение, иначе прежнее. */
const useDebounced = <T>(value: T, delay: number): T => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(timer);
  }, [value, delay]);
  return debounced;
};

export interface IContractInvitePreviewState {
  /** Последний пришедший предпросмотр (на время пере-запроса — прежний). */
  preview: IContractInvitePreview | null;
  /** Первый ответ ещё не пришёл. */
  loading: boolean;
  /** Предпросмотр отстаёт от правки: пауза в наборе или пере-запрос в пути. */
  refreshing: boolean;
  /** Сеть или отказ — предпросмотра нет. */
  failed: boolean;
  /** Ответ `null`: договор не найден или ведёт его не вызывающий. */
  missing: boolean;
  reload: () => void;
}

/**
 * ТЗ 0094 — письмо-приглашение так, как оно уйдёт при отправке договора
 * сейчас (`contractInvitePreview`): от кого, кому, своя или общая версия
 * шаблона, тема и текст с подставленными значениями (ссылка — образец).
 *
 * `override` — разовая правка (сырой текст с `{…}`): предпросмотр
 * пере-запрашивается с ней после паузы в наборе; ошибку правки сервер
 * возвращает в `errorCode`, текст при этом всё равно собирается.
 *
 * Без кэша: предпросмотр — снимок состояния ящика, шаблона и договора на
 * эту минуту, и при следующем открытии окна он должен быть свежим.
 */
export const useContractInvitePreview = (
  documentId: string,
  override: IInviteOverride | null,
  enabled = true,
): IContractInvitePreviewState => {
  // Строки, а не объект правки: новый объект на каждый рендер
  // перезапускал бы таймер без конца.
  const typedSubject = override?.subject ?? '';
  const typedBody = override?.body ?? '';
  const subject = useDebounced(typedSubject, INVITE_PREVIEW_DEBOUNCE_MS);
  const body = useDebounced(typedBody, INVITE_PREVIEW_DEBOUNCE_MS);
  // Правка ещё не дошла до запроса (пауза в наборе): показанный
  // предпросмотр — от прежнего текста, «Отправить» его не подтверждает.
  const behind = subject !== typedSubject || body !== typedBody;
  const variables = {
    documentId,
    ...(subject ? { emailSubject: subject } : {}),
    ...(body ? { emailBody: body } : {}),
  };
  const {
    data,
    previousData,
    loading,
    error,
    refetch,
  } = useEsignInvitePreviewQuery({
    variables,
    skip: !enabled,
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
  });
  const current = data ?? previousData;
  const preview = current?.contractInvitePreview ?? null;
  return {
    preview,
    loading: loading && !current,
    refreshing: (loading && Boolean(current)) || (enabled && behind),
    failed: Boolean(error) && !current,
    missing: Boolean(current) && preview === null && !loading,
    reload: () => {
      refetch().catch(() => undefined);
    },
  };
};
